import { css, Interpolation, Theme } from '@emotion/react';
import { upperFirst } from 'lodash';
import React from 'react';

import { Icon } from '../Icon';
import { Spinner } from '../Spinner';
import {
  labelStyles,
  rootStyles,
  spinnerStyles,
  styles,
} from './Button.styles';
import { ButtonVariants, SizeVariant } from './Button.types';

type ButtonAndAnchorProps = React.HTMLProps<HTMLButtonElement> &
  React.HTMLProps<HTMLAnchorElement>;

export type ButtonProps = {
  /**
   * Element type to render as (button, a, etc.)
   */
  as?: React.ElementType;
  /**
   * Visual style variant of the button
   */
  variant?: ButtonVariants;
  /**
   * Whether the button should take full width
   */
  fullWidth?: boolean;
  /**
   * Whether to show loading state
   */
  isLoading?: boolean;
  /**
   * Whether the button is disabled
   */
  disabled?: boolean;
  /**
   * Button content
   */
  children?: React.ReactNode;
  /**
   * Button type attribute
   */
  type?: 'submit' | 'button';
  /**
   * Size variant of the button
   */
  size?: 'xs' | 'base' | 'sm' | 'md' | 'lg';
  /**
   * Additional CSS class names
   */
  className?: string;
  /**
   * Custom styles for the button label
   */
  labelStyleOverrides?: Interpolation<Theme>;
  /**
   * Click event handler
   */
  onClick?: React.MouseEventHandler<HTMLElement>;
  /**
   * Icon element to display in the button
   */
  icon?: React.ReactElement<React.ComponentProps<typeof Icon>>;
} & Omit<ButtonAndAnchorProps, 'size' | 'as' | 'onClick'>;

const Button = React.forwardRef<HTMLElement, ButtonProps>(
  (
    {
      as: Component = 'button',
      variant = 'primary',
      fullWidth = false,
      size = 'base',
      disabled = false,
      children,
      type = 'button',
      isLoading = false,
      className = '',
      labelStyleOverrides = css``,
      icon,
      ...props
    },
    ref
  ) => {
    const componentType = Component === 'button' ? type : undefined;
    const sizeKey = `size${upperFirst(size)}` as SizeVariant;
    const isIconOnly = icon && !children;

    return (
      <Component
        ref={ref}
        type={componentType}
        className={className}
        css={[
          rootStyles,
          styles[variant],
          fullWidth && styles.fullWidth,
          size && styles[sizeKey],
          isIconOnly && size === 'xs' && styles.iconOnlyXs,
          isIconOnly && size === 'lg' && styles.iconOnlyLg,
          isIconOnly && size !== 'lg' && size !== 'xs' && styles.iconOnly,
          (isLoading || disabled) && styles.disabled,
          icon && children && styles.withIcon,
        ]}
        {...props}
        onClick={(e: React.MouseEvent<HTMLElement>) => {
          if (disabled) {
            e.preventDefault();
            return;
          }
          props.onClick?.(e);
        }}
      >
        {icon}
        {children && (
          <span css={[labelStyles(!isLoading), labelStyleOverrides]}>
            {children}
          </span>
        )}
        {isLoading && <Spinner css={spinnerStyles(variant)} />}
      </Component>
    );
  }
);

Button.displayName = 'Button';

export default Button;
